import { useTheme } from '@mui/material/styles';
import { Box, Grid, Divider, Drawer, Fab, Stack, Typography, RadioGroup, FormControlLabel, Radio, Button } from '@mui/material';
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import SimpleBar from 'components/third-party/SimpleBar';
import { Add, Setting2 } from 'iconsax-react';
import { HEADER_HEIGHT } from 'config';
import IndiaFlag from 'assets/images/common/INDIA.svg';
import USAFlag from 'assets/images/common/USA.svg';
import { ChangeEvent, useState } from 'react';
import { useDataContext } from 'contexts/DataContext';
import { Country } from 'types/custom/data';

type Props = {
  handleToggle: () => void;
  open: boolean;
};

const SettingsMenu: React.FC<Props> = ({ handleToggle, open }) => {
  const theme = useTheme();
  const { country: countryGlobal, setCountry: setCountryGlobal } = useDataContext();
  const [country, setCountry] = useState<Country>(countryGlobal);

  // Handle country change and update local storage
  const handleCountryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newCountry = event.target.value;
    setCountry(newCountry as unknown as Country);
  };

  const handleApply = () => {
    setCountryGlobal(country);
    window.location.reload();
  };

  const handleCancel = () => {
    handleToggle();
  };

  return (
    <>
      <Fab
        component="div"
        onClick={handleToggle}
        size="large"
        variant="circular"
        sx={{
          borderRadius: 0,
          borderTopLeftRadius: '50%',
          borderBottomLeftRadius: '50%',
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px',
          top: '14%',
          position: 'fixed',
          right: 0,
          zIndex: 1200,
          boxShadow: theme.customShadows.z1,
          bgcolor: 'background.paper',
          border: `4px solid ${theme.palette.background.paper}`,
          borderRight: 'none',
          '&:hover': {
            bgcolor: 'primary.lighter'
          }
        }}
      >
        <IconButton onClick={handleToggle} aria-label="settings toggler" size="large" sx={{ p: 0, '& svg': { width: 28, height: 28 } }}>
          <Setting2 variant="Bulk" />
        </IconButton>
      </Fab>
      <Drawer sx={{ zIndex: 2001 }} anchor="right" onClose={handleToggle} open={open} PaperProps={{ sx: { width: 350 } }}>
        <MainCard content={false} border={false}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ p: 2.5, mt: 10 }}>
            <Typography variant="h5">Settings</Typography>
            <IconButton color="error" sx={{ p: 0 }} onClick={handleToggle}>
              <Add size={28} style={{ transform: 'rotate(45deg)' }} />
            </IconButton>
          </Stack>
          <SimpleBar sx={{ '& .simplebar-content': { display: 'flex', flexDirection: 'column' } }}>
            <Box sx={{ p: 3, height: `calc(100vh - ${HEADER_HEIGHT}px)` }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Stack>
                    <Typography variant="h4" color="textPrimary">
                      Country
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Choose country
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <RadioGroup row aria-label="country" name="country" value={country} onChange={handleCountryChange}>
                    <Stack direction="row" alignItems="center" spacing={2.5} sx={{ width: '100%' }}>
                      <FormControlLabel
                        control={<Radio value={Country.in} sx={{ display: 'none' }} />}
                        sx={{ width: '100%', height: 60, m: 0, display: 'flex', '& .MuiFormControlLabel-label': { flex: 1 } }}
                        label={
                          <MainCard
                            content={false}
                            sx={{ borderWidth: 2, p: 1, ...(country === Country.in && { borderColor: theme.palette.primary.main }) }}
                          >
                            <Stack direction="row" alignItems="center" justifyContent="center" sx={{ height: 44 }}>
                              <img src={IndiaFlag} alt="India Flag" style={{ width: 40, height: 40 }} />
                            </Stack>
                          </MainCard>
                        }
                      />
                      <FormControlLabel
                        control={<Radio value={Country.us} sx={{ display: 'none' }} />}
                        sx={{ width: '100%', height: 60, m: 0, display: 'flex', '& .MuiFormControlLabel-label': { flex: 1 } }}
                        label={
                          <MainCard
                            content={false}
                            sx={{ borderWidth: 2, p: 1, ...(country === Country.us && { borderColor: theme.palette.primary.main }) }}
                          >
                            <Stack direction="row" alignItems="center" justifyContent="center" sx={{ height: 44 }}>
                              <img src={USAFlag} alt="USA Flag" style={{ width: 40, height: 40 }} />
                            </Stack>
                          </MainCard>
                        }
                      />
                    </Stack>
                  </RadioGroup>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button size="small" onClick={handleCancel} color="secondary" variant="outlined">
                      Cancel
                    </Button>
                    <Button size="small" color="success" variant="contained" onClick={handleApply} autoFocus>
                      Apply
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
              </Grid>
            </Box>
          </SimpleBar>
        </MainCard>
      </Drawer>
    </>
  );
};

export default SettingsMenu;
